import { Bell, FolderOpen, Mail, Menu, Moon, Search, Sun } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAnalyseDomain } from '@/features/brandAnalyser';
import { useAlertSummary } from '@/features/notifications';
import { SUPER_ADMIN } from '@/features/auth/roles';
import { isRouteActive } from '@/app/navigation/activeRoute';
import { breadcrumbFor } from '@/app/navigation/navItems';
import { ROUTES } from '@/config/routes';
import { cn } from '@/shared/lib/cn';
import { useTheme } from '@/components/ThemeProvider';

import { AccountMenu } from './AccountMenu';

export interface AppTopBarProps {
  sidebarCollapsed: boolean;
  onOpenMobileMenu: () => void;
  /**
   * Receives the element a page's own header control is portaled into.
   *
   * <p>A callback ref rather than a `RefObject`, because the layout has to **re-render**
   * when the element appears: it puts the element into a context, and a mutable ref
   * changing does not tell React to. Optional, so the bar renders in a test with no slot.
   */
  onSlotReady?: (element: HTMLDivElement | null) => void;
  /**
   * Whether the current screen has put a control in the slot.
   *
   * <p>When it has not, Email Warmup screens show a fallback "All accounts" button — the
   * design's rule, gated on the same count rather than on a list of which screens have
   * their own picker, so a screen that gains one needs no edit here.
   */
  headerSlotFilled?: boolean;
  /**
   * The role keys this session holds, from `/account/me`.
   *
   * Empty while the request is in flight. The direction matters less here than it does
   * on the rail: it means the operator sees the tenant controls for a moment on a cold
   * load, rather than a tenant seeing something they should not.
   */
  roleKeys: ReadonlySet<string>;
}

/**
 * The bar across the top of the signed-in application.
 *
 * <h2>Every class in here is copied from the design, not written to match it</h2>
 *
 * `the previous product's AppNav.tsx`, lines 1555 to 1900. That includes the four
 * **skin hook classes** this bar carries and had none of until 8 October 2026 —
 * `app-top-navbar`, `neuDark`, `nav-link-button` and `search-input` — all four of which are
 * defined in this repository's own `index.css` and were simply dropped when the bar was
 * rebuilt from the design.
 *
 * <p><b>`nav-link-button` is the load-bearing one.</b> `index.css` carries
 * `.dark button:not(…fourteen classes…)` with `!important` on `color` and `border-color`,
 * and a second rule doing the same to `:hover`. A plain `<button>` on the dark theme is
 * therefore repainted by the skin whatever it asks for, and `nav-link-button` is one of the
 * fourteen names on the exclusion list — which then gets `background-color: #262626` of its
 * own. The three controls here were plain buttons, so on dark they were the generic skin
 * button rather than the design's. The rail's collapse control carries
 * `sidebar-toggle-button` for the same reason.
 *
 * <p>The design's controls are shadcn `Button`s, which bring `inline-flex items-center
 * justify-center` with them. These are plain `<button>` elements, so they say it: without
 * it a 14px inline SVG in a 34px box sits on the text baseline rather than in the middle.
 *
 * <h2>`size-4`, because `h-[19px]` in the design is a class that does nothing</h2>
 *
 * The design's controls are shadcn `Button`s, and that component's base carries
 * `[&_svg:not([class*='size-'])]:size-4`. Tailwind compiles it to
 *
 * ```css
 * .…size-4 svg:not([class*='size-']) { width: calc(var(--spacing)*4); height: … }
 * ```
 *
 * — specificity **(0,2,1)**, against **(0,1,0)** for the icon's own `.h-\[19px\]`. So every
 * icon in that header renders at `size-4` = 1rem = **14px**, and the `h-[19px] w-[19px]`
 * written beside it is dead. Its `<Menu>` and the rail's collapse chevron say `h-5 w-5` and
 * render at 14px for the same reason.
 *
 * <p>These are plain `<button>` elements with no such base rule, so copying that class
 * copied the number rather than the result: the icons rendered at a literal 19px, **36%
 * larger than the design's**, and the buttons around them were the right size — which is why
 * comparing the controls' positions in a screenshot showed no difference at all.
 *
 * <p>`size-4` rather than `h-[14px] w-[14px]`: it is the value the design actually resolves,
 * it goes through `--spacing` so it tracks the root font exactly as the design's does, and
 * an icon carrying `size-` is the one thing that base rule leaves alone — so this stays
 * correct if a control ever becomes a `Button`.
 *
 * <p>The magnifier in the search field is the exception and is genuinely 18px: the design
 * puts it in a plain `<button>`, not a `Button`, so its `h-[18px] w-[18px]` applies. The
 * rail's rows are plain links for the same reason and are genuinely 20px.
 *
 * <h2>The three controls are NOT one shared class list</h2>
 *
 * They were, as a `CONTROL` constant, on the reasoning that repeating a long class list is
 * how one of them ends up subtly different. The design has three different lists —
 * the bell hovers `bg-gray-100` with `border-gray-200`, the other two hover `bg-slate-50`
 * with `border-slate-200` — so the constant was the thing making them differ from the
 * design in order to stop them differing from each other.
 *
 * <h2>It sits beside the sidebar, not above it</h2>
 *
 * `left` tracks the rail's width so the two move together when it collapses.
 * Spanning the full width instead would put the search field under the logo, and
 * the collapse would slide content beneath a bar that did not move.
 *
 * <h2>Its content is scaled, and the rail is not</h2>
 *
 * Everything in here is in absolute pixels — a 34px control, a 14px breadcrumb, a 308px
 * field — and the design renders the whole of it at 0.85 to 0.90 through `--ui-scale`,
 * applied with CSS `zoom`. Unscaled, this bar was about 9% larger than the design's,
 * everywhere at once. `useUiScale` publishes the variable from `AppLayout`.
 *
 * <h2>The platform operator gets the bar, without the tenant controls on it</h2>
 *
 * The brand search and the Collections shortcut are Brand Intelligence — a product,
 * belonging to a company, and the operator belongs to the platform's own company. A
 * disabled search field somebody cannot use is the loudest thing on a page whose whole
 * point is the list underneath it.
 *
 * <p><b>The bar itself stays, and that is deliberate.</b> It carries the account menu,
 * and the account menu carries sign-out. Removing the header would take the only way out
 * of the application with it.
 *
 * <h2>The breadcrumb is the left half, and the search is not</h2>
 *
 * The bar carried a wide "Analyze domain or URL" field on the left and nothing else until
 * 8 October 2026, which left the page with no name anywhere on it. The design puts
 * `Workspace / <page>` there and a 308px `Search Brand…` field on the right beside the
 * controls. The breadcrumb's last segment is the only part in the ink colour, so the eye
 * reads the page rather than the trail; {@link breadcrumbFor} builds it from the menu.
 *
 * <h2>A screen can put its own control beside the breadcrumb</h2>
 *
 * `onSlotReady` hands out an element that {@link HeaderSlot} portals into, 25px to the
 * right of the trail — the design's `gap-[25px]`, and the mechanism its `AppHeaderSlot`
 * uses. Nothing fills it yet; Email Warmup's "All accounts" picker is what it is for.
 *
 * <h2>What is deliberately not carried across</h2>
 *
 * The design wraps each control in a Radix tooltip, puts a real search behind the field,
 * and adds `navbar-scrolled` from a scroll listener. The tooltips say what the `aria-label`
 * already says; there is no search endpoint yet, so the field is disabled; and
 * `.navbar-scrolled` is defined only under the two glass themes, so a scroll listener here
 * would maintain state nothing reads.
 *
 * <p>Nor is the design's **mobile search button** — a `xl:hidden` magnifier that opens a
 * full-width search row — since there is nothing behind it. That leaves this bar with no
 * search affordance below `xl` where the design has one, which is a visible difference and
 * is recorded rather than filled with a dead control.
 */
export function AppTopBar({
  sidebarCollapsed,
  onOpenMobileMenu,
  roleKeys,
  onSlotReady,
  headerSlotFilled = false,
}: AppTopBarProps) {
  const isOperator = roleKeys.has(SUPER_ADMIN);
  // The bell's badge. Asked again every minute while the shell is open; a failure shows no badge.
  const unread = useAlertSummary().data?.unread ?? 0;

  /*
   * The LEGACY theme context, not `@/shared/theme/useTheme`, and the distinction is
   * load-bearing.
   *
   * Only one theme provider is actually mounted -- the ported one, in AppProviders.
   * Ours is not, despite CLAUDE.md saying both run: it stores under
   * `xtremand.theme` while the ported one uses `xtremand-theme`, which is also the key
   * index.html's blocking pre-paint script reads. Mounting ours alongside would put
   * two disagreeing sources on one `dark` class, and the pre-paint ground colour
   * would follow whichever the script happened to read.
   *
   * So the shell reads the provider that is genuinely in control. Collapsing the
   * two is the refactor; picking the wrong one is a white page on the dark theme.
   */
  const { theme, actualTheme, setTheme } = useTheme();
  const dark = actualTheme === 'dark';

  /*
   * The design's own flag. The two glass themes are real values of this provider's `Theme`
   * -- a person who chose one in the application being replaced still has it, carried over
   * by shared/storage/legacyStorageMigration.ts when the storage key was renamed
   * -- and the skin's glass rules key on a class the provider puts on the root, so
   * the bar has to pick the glass colours itself where the design does.
   */
  const isGlass = theme === 'light-glass' || theme === 'dark-glass';

  const { analyse, isAnalysing } = useAnalyseDomain();
  const [domainInput, setDomainInput] = useState('');
  const submitSearch = (event: SyntheticEvent) => {
    event.preventDefault();
    void analyse(domainInput).then((done) => {
      if (done) setDomainInput('');
    });
  };

  const { pathname } = useLocation();
  const trail = breadcrumbFor(pathname);

  /*
   * Every Email Warmup screen, not only the list -- `isRouteActive` matches descendants,
   * so the create and detail routes count. The design asks the same question with
   * `isEmailWarmupRoute`.
   */
  const isEmailWarmupRoute = isRouteActive(ROUTES.emailWarmup, pathname);

  return (
    <header
      className={cn(
        'app-top-navbar neuDark fixed top-0 right-0 left-0 z-30 flex h-16 flex-shrink-0 items-center border-b pl-4 transition-all duration-500 ease-out lg:pl-0',
        sidebarCollapsed ? 'lg:left-17.5' : 'lg:left-65',
        isGlass
          ? 'border-white/10 bg-transparent'
          : 'border-slate-200 bg-white dark:border-white/10 dark:bg-[#0c182d]',
      )}
    >
      <button
        type="button"
        onClick={onOpenMobileMenu}
        aria-label="Open navigation menu"
        className="mobile-menu-button mr-1 -ml-2 inline-flex h-8 items-center justify-center rounded-md border border-gray-200 p-2 hover:bg-gray-50 lg:hidden dark:border-white/10 dark:hover:bg-white/5"
      >
        <Menu aria-hidden className="size-4 text-gray-700 dark:text-gray-300" />
      </button>

      {/*
        `zoom`, not `transform: scale()`: zoom scales the LAYOUT, so the contents genuinely
        become smaller inside the 64px header. A transform would shrink the painting and
        leave every box reserving its original space.

        An inline style because it is one declaration reading one variable; a utility class
        would be a second place to look for a number that only exists at runtime.
      */}
      <div
        className="flex flex-1 items-center justify-between pr-[16px] pl-[12px] md:pr-[27px] md:pl-[28px]"
        style={{ zoom: 'var(--ui-scale, 1)' }}
      >
        {/*
          The breadcrumb and whatever the current screen puts beside it, 25px apart -- the
          design's `gap-[25px]`. Both are `hidden md:flex`: below that width the bar has
          room for the controls and nothing else.
        */}
        <div className="hidden min-w-0 items-center gap-[25px] md:flex">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-[9px] text-[14px] whitespace-nowrap text-slate-500 dark:text-gray-400"
          >
            {trail.map((segment, index) => (
              <span key={segment} className="flex items-center gap-[9px]">
                {index === 0 ? null : (
                  <span aria-hidden className="text-slate-400 dark:text-gray-500">
                    /
                  </span>
                )}
                <span
                  className={cn(
                    index === trail.length - 1 && 'font-medium text-slate-900 dark:text-gray-100',
                  )}
                >
                  {segment}
                </span>
              </span>
            ))}
          </nav>

          {/*
            The current screen's own control, portaled in by `HeaderSlot`. Empty on every
            screen today -- `empty:hidden` means an empty slot takes no space and the
            `gap-[25px]` beside it collapses, so the bar is unchanged until something
            fills it. Email Warmup's "All accounts" picker is what it is for.
          */}
          <div ref={onSlotReady} className="flex items-center empty:hidden" />

          {/*
            The fallback, for an Email Warmup screen that brought no picker of its own —
            create, and the detail screen until it grows one. `AppNav.tsx:1588-1597`.

            It NAVIGATES; it is not a dropdown. The design's is a ghost `Button` with an
            `onClick` that goes to the list, and the only control that opens a menu is the
            list's own picker, which arrives through the slot above.

            `nav-link-button` and `neumorphic-card` are the skin's hook classes and are
            load-bearing: `index.css` repaints every `<button>` not on its exclusion list
            with `!important` on the dark theme, and `nav-link-button` is on that list.
          */}
          {!headerSlotFilled && isEmailWarmupRoute ? (
            <Link
              to={ROUTES.emailWarmup}
              className="nav-link-button neumorphic-card inline-flex h-[34px] items-center gap-[10px] rounded-[8px] border border-slate-200 bg-white px-[14px] text-[13px] font-medium text-slate-800 hover:bg-slate-50 dark:border-white/10 dark:bg-transparent dark:text-gray-200"
            >
              <Mail aria-hidden className="h-[17px] w-[17px] text-slate-600 dark:text-gray-400" />
              All accounts
            </Link>
          ) : null}
        </div>

        <div className="flex-1" />

        <div className="ml-[16px] flex min-w-0 items-center gap-[16px]">
          {/*
            The brand search. `xl:` only, like the design: below that width the breadcrumb
            and four controls already fill the bar, and a field squeezed between them is
            narrower than its own placeholder.

            Submitting analyses the typed domain through `POST /api/forward` -- there is no
            lookup of stored brands, so this is "analyse", not "search". Enter or the
            magnifier runs it.
          */}
          {isOperator ? null : (
            <form
              role="search"
              onSubmit={submitSearch}
              className="search-container relative hidden w-[308px] min-w-[150px] shrink xl:flex"
            >
              <div className="relative w-full">
                <label htmlFor="domain-search" className="sr-only">
                  Search Brand
                </label>
                <button
                  type="submit"
                  aria-label="Analyse this domain"
                  disabled={isAnalysing || !domainInput.trim()}
                  className="nav-link-button absolute inset-y-0 left-0 flex items-center pl-[14px] text-slate-500 disabled:cursor-default"
                >
                  <Search aria-hidden className="g-color h-[18px] w-[18px]" />
                </button>
                <input
                  id="domain-search"
                  type="text"
                  value={domainInput}
                  onChange={(event) => {
                    setDomainInput(event.target.value);
                  }}
                  disabled={isAnalysing}
                  autoComplete="off"
                  className={cn(
                    'search-input neumorphic-card block h-[34px] w-full rounded-[8px] border pr-[34px] pl-[45px] text-[14px] placeholder-slate-500 transition-all duration-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none',
                    isGlass
                      ? 'glass-bg g-color light-border'
                      : 'border-slate-200 bg-white dark:border-white/10 dark:bg-white/5 dark:text-gray-100 dark:placeholder-gray-400',
                  )}
                  placeholder="Search Brand..."
                />
              </div>
            </form>
          )}

          <Link
            to={ROUTES.alerts}
            aria-label={unread > 0 ? `Notifications, ${String(unread)} unread` : 'Notifications'}
            className={cn(
              'nav-link-button neumorphic-card group relative inline-flex h-[34px] w-[38px] items-center justify-center rounded-[8px] border p-0 transition-colors duration-200',
              isGlass
                ? 'glass-hover border-white/10'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-100 dark:border-white/10',
            )}
          >
            <Bell
              aria-hidden
              className={cn(
                'size-4 transition-colors duration-200',
                isGlass
                  ? 'g-color'
                  : 'text-slate-700 group-hover:text-slate-900 dark:text-gray-300',
              )}
            />
            {unread > 0 ? (
              <span
                aria-hidden
                className="absolute -top-1.5 -right-1.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white"
              >
                {unread > 99 ? '99+' : unread}
              </span>
            ) : null}
          </Link>

          {isOperator ? null : (
            <Link
              to={ROUTES.collections}
              aria-label="Collections"
              className="nav-link-button neumorphic-card group flex h-[34px] w-[38px] items-center justify-center rounded-[8px] border border-slate-200 p-0 hover:border-slate-300 hover:bg-slate-50 dark:border-white/10"
            >
              <FolderOpen
                aria-hidden
                className="g-color size-4 text-slate-700 transition-colors duration-200 group-hover:text-slate-900 dark:text-gray-300"
              />
            </Link>
          )}

          {/*
            The one control here that does something, because it can: the whole answer is
            one setter, and a theme toggle that shows the theme but refuses to change it is
            worse than none.

            The icon shows what you HAVE, which is the design's `renderThemeIcon` -- an
            amber sun on light, a blue moon on dark -- rather than what clicking would give
            you. `text-blue-500` is the design's; this had `text-blue-300`.
          */}
          <button
            type="button"
            aria-label={dark ? 'Switch to the light theme' : 'Switch to the dark theme'}
            onClick={() => {
              setTheme(dark ? 'light' : 'dark');
            }}
            className="nav-link-button neumorphic-card group relative inline-flex h-[34px] w-[38px] items-center justify-center rounded-[8px] border border-slate-200 p-0 transition-colors duration-200 hover:border-slate-300 hover:bg-slate-50 dark:border-white/10"
          >
            {dark ? (
              <Moon aria-hidden className="size-4 text-blue-500 transition-colors duration-200" />
            ) : (
              <Sun aria-hidden className="size-4 text-amber-500 transition-colors duration-200" />
            )}
          </button>

          <AccountMenu />
        </div>
      </div>
    </header>
  );
}
